import { useState, useEffect } from "react";
import { Card, Table, Row, Col, Typography, Button, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { BarChart3, Users } from "lucide-react";
import dayjs from "dayjs";

import useAnalytics from "../hooks/useAnalytics";
import PageDocumentation from "../components/PageDocumentation";
import PeriodSelector from "../components/analytics/PeriodSelector";
import StatCard from "../components/analytics/StatCard";
import ProfitByBusChart from "../components/analytics/ProfitByBusChart";
import IncomeExpensesChart from "../components/analytics/IncomeExpensesChart";
import BusPassengersModal from "../components/analytics/BusPassengersModal";

const { Title } = Typography;

function BusesAnalytics() {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const [passengersModalOpen, setPassengersModalOpen] = useState(false);
  const [selectedBusId, setSelectedBusId] = useState(null);

  const {
    busesData,
    timelineData,
    busPassengersData,
    loading,
    getBusesAnalytics,
    getIncomeExpensesTimeline,
    getBusPassengers,
  } = useAnalytics();

  const handleViewPassengers = (busId) => {
    setSelectedBusId(busId);
    setPassengersModalOpen(true);
    getBusPassengers(busId);
  };

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      getBusesAnalytics(startDate, endDate);
      getIncomeExpensesTimeline(startDate, endDate);
    }
  }, [dateRange]);

  const handlePeriodChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    }
  };

  const columns = [
    {
      title: "Fecha",
      dataIndex: "date",
      key: "date",
      width: 100,
      render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Excursión",
      dataIndex: "excursion_name",
      key: "excursion_name",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Guía",
      dataIndex: "guide_name",
      key: "guide_name",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Bus",
      dataIndex: "bus_name",
      key: "bus_name",
      width: 140,
      ellipsis: true,
    },
    {
      title: (
        <Tooltip title="Suma de lo que deben pagar los pasajeros">
          <span>Total <InfoCircleOutlined className="text-gray-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: "total_theoretical",
      key: "total_theoretical",
      width: 95,
      align: "right",
      render: (value) => (
        <span className="text-gray-600">${value?.toLocaleString() || 0}</span>
      ),
    },
    {
      title: (
        <Tooltip title="Suma de todos los pagos recibidos de pasajeros">
          <span>Cobrado <InfoCircleOutlined className="text-gray-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: "total_collected",
      key: "total_collected",
      width: 95,
      align: "right",
      render: (value) => (
        <span className="text-green-600">${value?.toLocaleString() || 0}</span>
      ),
    },
    {
      title: (
        <Tooltip title="Total - Cobrado (lo que falta cobrar)">
          <span>Por Cobrar <InfoCircleOutlined className="text-gray-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: "total_debt",
      key: "total_debt",
      width: 105,
      align: "right",
      render: (value) => (
        <span className={value > 0 ? "text-orange-500" : "text-green-600"}>
          ${value?.toLocaleString() || 0}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="Pagos recibidos por vendedores/guías que aún no fueron rendidos a oficina">
          <span>Sin Rendir <InfoCircleOutlined className="text-gray-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: "total_pending_settlement",
      key: "total_pending_settlement",
      width: 100,
      align: "right",
      render: (value) => (
        <span className={value > 0 ? "text-yellow-600" : "text-gray-400"}>
          ${value?.toLocaleString() || 0}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="Paga guía + Transportista + Gastos fijos + Adicionales + Taxi vendedor">
          <span>Gastos <InfoCircleOutlined className="text-gray-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: "total_expenses",
      key: "total_expenses",
      width: 95,
      align: "right",
      render: (value) => (
        <span className="text-red-500">${value?.toLocaleString() || 0}</span>
      ),
    },
    {
      title: (
        <Tooltip title="Cobrado - Gastos">
          <span>Ganancia <InfoCircleOutlined className="text-gray-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: "profit",
      key: "profit",
      width: 100,
      align: "right",
      render: (value) => (
        <span className={value >= 0 ? "text-blue-600 font-bold" : "text-red-600 font-bold"}>
          ${value?.toLocaleString() || 0}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleViewPassengers(record.id)}
        >
          Ver detalle
        </Button>
      ),
    },
  ];

  const totals = busesData?.totals || {
    total_theoretical: 0,
    total_collected: 0,
    total_debt: 0,
    total_expenses: 0,
    profit: 0,
    total_pending_settlement: 0,
    trips_count: 0,
  };

  const isLoading = loading.buses || loading.timeline;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 size={24} className="text-blue-600" />
          <Title level={3} className="!mb-0">
            Ganancia por micro
          </Title>
        </div>
        <PageDocumentation docKey="buses-analytics" />
      </div>

      <PeriodSelector
        value={dateRange}
        onChange={handlePeriodChange}
        loading={isLoading}
      />

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} md={4}>
          <StatCard
            title="Total Teórico"
            value={totals.total_theoretical}
            type="neutral"
            loading={loading.buses}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <StatCard
            title="Cobrado"
            value={totals.total_collected}
            type="income"
            loading={loading.buses}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <StatCard
            title="Por Cobrar"
            value={totals.total_debt}
            type="warning"
            loading={loading.buses}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <StatCard
            title="Gastos"
            value={totals.total_expenses}
            type="expenses"
            loading={loading.buses}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <StatCard
            title="Ganancia"
            value={totals.profit}
            type="profit"
            loading={loading.buses}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <StatCard
            title="Viajes"
            value={totals.trips_count}
            prefix=""
            type="neutral"
            loading={loading.buses}
          />
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} lg={12}>
          <ProfitByBusChart
            data={busesData?.trips || []}
            loading={loading.buses}
          />
        </Col>
        <Col xs={24} lg={12}>
          <IncomeExpensesChart
            data={timelineData?.by_date || []}
            loading={loading.timeline}
          />
        </Col>
      </Row>

      {/* Table */}
      <Card title="Detalle por Viaje">
        <Table
          columns={columns}
          dataSource={busesData?.trips || []}
          rowKey="id"
          loading={loading.buses}
          pagination={{ pageSize: 20 }}
          size="small"
          scroll={{ x: 1150 }}
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest('.ant-btn, a')) return;
              handleViewPassengers(record.id);
            },
            style: { cursor: 'pointer' }
          })}
          summary={() =>
            busesData?.trips?.length > 0 ? (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-100 font-bold">
                  <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3}>
                    {totals.trips_count} viajes
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <span className="text-gray-600">
                      ${totals.total_theoretical?.toLocaleString() || 0}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <span className="text-green-600">
                      ${totals.total_collected?.toLocaleString() || 0}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <span className={totals.total_debt > 0 ? "text-orange-500" : "text-green-600"}>
                      ${totals.total_debt?.toLocaleString() || 0}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <span className={totals.total_pending_settlement > 0 ? "text-yellow-600" : "text-gray-400"}>
                      ${totals.total_pending_settlement?.toLocaleString() || 0}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    <span className="text-red-500">
                      ${totals.total_expenses?.toLocaleString() || 0}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    <span className="text-blue-600">
                      ${totals.profit?.toLocaleString() || 0}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      </Card>

      {/* Passengers Modal */}
      <BusPassengersModal
        open={passengersModalOpen}
        onClose={() => setPassengersModalOpen(false)}
        data={busPassengersData}
        loading={loading.busPassengers}
      />
    </div>
  );
}

export default BusesAnalytics;
